import { getHTMLFromFragment } from '@tiptap/react';
import { Fragment, Mark, type Node as PMNode, type ResolvedPos, type Schema } from '@tiptap/pm/model';

// A size change, the part of it that is plain text, and how far both may be off.
export interface SizeEstimate {
  delta: number;
  textDelta: number;
  drift: number;
}

// Bytes that content adds to a document's HTML and plain text.
interface ContentBytes {
  html: number;
  text: number;
}

// How far a block-level estimate can be off; inline estimates are exact. Block frames are
// closed nodes, whose HTML does not depend on their siblings, so only the plain-text block
// separators differ: a frame's text omits the separator before its first block, which the
// document has unless no block precedes the frame, and that cancels out between the old and new
// frame unless exactly one of them holds no block. That is one byte of plain text.
const BLOCK_EDIT_DRIFT = 1;

// Size change from `oldDoc` to `newDoc`, measured only on the parts of each that enclose the
// changed range. Returns undefined when those parts do not line up.
export const sizeDelta = (oldDoc: PMNode, newDoc: PMNode): SizeEstimate | undefined => {
  const range = changedRange(oldDoc, newDoc);
  if (!range) return { delta: 0, textDelta: 0, drift: 0 };

  const $oldStart = oldDoc.resolve(range.start);
  const $newStart = newDoc.resolve(range.start);
  const depth = Math.min($oldStart.sharedDepth(range.endOld), $newStart.sharedDepth(range.endNew));
  // Content before the range is the same in both docs, so the node at `depth` has the same type
  // in both.
  if ($oldStart.node(depth).inlineContent) {
    return inlineDelta($oldStart, $newStart, oldDoc.resolve(range.endOld), newDoc.resolve(range.endNew), depth);
  }

  const oldFrame = blockFrame($oldStart, oldDoc.resolve(range.endOld), depth);
  const newFrame = blockFrame($newStart, newDoc.resolve(range.endNew), depth);
  // Everything outside the frames must be identical: same prefix length and same suffix length.
  const framesAlign = oldFrame.from === newFrame.from && oldDoc.content.size - oldFrame.to === newDoc.content.size - newFrame.to;
  if (!framesAlign) return undefined;

  const oldContent = oldDoc.slice(oldFrame.from, oldFrame.to).content;
  const newContent = newDoc.slice(newFrame.from, newFrame.to).content;
  const drift = hasSeparatedBlock(oldContent) === hasSeparatedBlock(newContent) ? 0 : BLOCK_EDIT_DRIFT;
  return estimateBetween(fragmentSize(oldContent, oldDoc.type.schema), fragmentSize(newContent, newDoc.type.schema), drift);
};

export const changedRange = (oldDoc: PMNode, newDoc: PMNode) => {
  const start = oldDoc.content.findDiffStart(newDoc.content);
  if (start === null) return null;

  const end = oldDoc.content.findDiffEnd(newDoc.content);
  if (!end) return null;

  // When the change repeats content next to it, the matched suffix can reach back past `start`.
  const overlap = Math.max(0, start - Math.min(end.a, end.b));
  return { start, endOld: end.a + overlap, endNew: end.b + overlap };
};

// Size change of a change inside one textblock, measured on the run of the textblock's children
// that the change touches. Inline content serializes as one sequence in which a node's mark
// wrappers depend only on its marks and the marks of the node before it, so the run's HTML
// difference is the document's when the run starts and ends with the same marks in both docs;
// where it does not, the run takes in the unchanged neighbour on that side. The text of inline
// content has no block separators, so the run's text difference is the document's too.
const inlineDelta = (
  $oldStart: ResolvedPos,
  $newStart: ResolvedPos,
  $oldEnd: ResolvedPos,
  $newEnd: ResolvedPos,
  depth: number
): SizeEstimate => {
  const oldBlock = $oldStart.node(depth);
  const newBlock = $newStart.node(depth);

  // The children before `first` and the last `kept` children are the same in both docs.
  let first = Math.min($oldStart.index(depth), $newStart.index(depth));
  const kept = Math.min(oldBlock.childCount - endIndex($oldEnd, depth), newBlock.childCount - endIndex($newEnd, depth));
  let oldEnd = oldBlock.childCount - kept;
  let newEnd = newBlock.childCount - kept;

  if (oldEnd - first === 1 && newEnd - first === 1) {
    const oldChild = oldBlock.child(first);
    const newChild = newBlock.child(first);
    if (oldChild.isText && newChild.isText && Mark.sameSet(oldChild.marks, newChild.marks)) {
      const childStart = $oldStart.posAtIndex(first, depth);
      const offset = $oldStart.pos - childStart;
      return estimateBetween(
        textChangeSize(oldChild.text ?? '', offset, $oldEnd.pos - childStart),
        textChangeSize(newChild.text ?? '', offset, $newEnd.pos - childStart),
        0
      );
    }
  }

  const sameMarks = (oldIndex: number, newIndex: number) =>
    first < oldEnd && first < newEnd && Mark.sameSet(oldBlock.child(oldIndex).marks, newBlock.child(newIndex).marks);
  if (first > 0 && !sameMarks(first, first)) first -= 1;
  if (kept > 0 && !sameMarks(oldEnd - 1, newEnd - 1)) {
    oldEnd += 1;
    newEnd += 1;
  }

  const schema = oldBlock.type.schema;
  return estimateBetween(
    fragmentSize(childrenBetween(oldBlock, first, oldEnd), schema),
    fragmentSize(childrenBetween(newBlock, first, newEnd), schema),
    0
  );
};

// Index of the first child of the node at `depth` that starts at or after `$pos`.
const endIndex = ($pos: ResolvedPos, depth: number) => {
  const inside = $pos.depth > depth || $pos.textOffset > 0;
  return $pos.index(depth) + (inside ? 1 : 0);
};

const estimateBetween = (before: ContentBytes, after: ContentBytes, drift: number): SizeEstimate => ({
  delta: after.html + after.text - before.html - before.text,
  textDelta: after.text - before.text,
  drift,
});

// Size of `text` between `from` and `to` in the HTML and the plain text. ProseMirror's diff never
// ends inside a surrogate pair, whose UTF-8 length is not the sum of its halves'.
const textChangeSize = (text: string, from: number, to: number): ContentBytes => {
  const changed = text.slice(from, to);
  return { html: htmlTextByteLength(changed), text: byteLength(changed) };
};

const childrenBetween = (node: PMNode, from: number, to: number) => {
  const children: PMNode[] = [];
  for (let index = from; index < to; index++) children.push(node.child(index));
  return Fragment.fromArray(children);
};

// The run of children of the node at `depth` that covers the range.
const blockFrame = ($from: ResolvedPos, $to: ResolvedPos, depth: number) => ({
  from: $from.depth > depth ? $from.before(depth + 1) : $from.pos,
  to: $to.depth > depth ? $to.after(depth + 1) : $to.pos,
});

// Whether `fragment` holds a block that the plain text separates from the blocks before it: a
// textblock, or a leaf block with text (as `textBetween` decides).
const hasSeparatedBlock = (fragment: Fragment) => {
  let found = false;
  fragment.descendants((node) => {
    if (found) return false;
    found = node.isTextblock || (node.isBlock && node.isLeaf && !!node.type.spec.leafText?.(node));
    return !found && !node.isTextblock;
  });
  return found;
};

// Size of closed nodes, as they add to a document's HTML and plain text (less the block
// separator before the first block).
const fragmentSize = (fragment: Fragment, schema: Schema): ContentBytes => ({
  html: htmlByteLength(fragment, schema),
  text: byteLength(fragment.textBetween(0, fragment.size, ' ')),
});

// HTML bytes of `fragment`, serialized with every text replaced by a one-character placeholder
// and the texts' escaped bytes counted as strings, so that serialization costs follow the node
// count rather than the text length.
const htmlByteLength = (fragment: Fragment, schema: Schema) => {
  let textBytes = 0;
  const withPlaceholders = (content: Fragment): Fragment => {
    const children: PMNode[] = [];
    content.forEach((node) => {
      if (node.isText) {
        textBytes += htmlTextByteLength(node.text ?? '') - 1;
        children.push(schema.text('x', node.marks));
      } else {
        children.push(node.copy(withPlaceholders(node.content)));
      }
    });
    return Fragment.fromArray(children);
  };
  return byteLength(getHTMLFromFragment(withPlaceholders(fragment), schema)) + textBytes;
};

// UTF-8 bytes of text in serialized HTML. Serializing a text node escapes exactly these four
// characters (HTML fragment serialization, "escaping a string" outside attribute mode).
const htmlTextByteLength = (text: string) => {
  let bytes = byteLength(text);
  for (let index = 0; index < text.length; index++) bytes += escapeBytes(text.charCodeAt(index));
  return bytes;
};

// Bytes an HTML escape adds over the character's own UTF-8 bytes.
export const escapeBytes = (code: number) => {
  if (code === 0x26) return '&amp;'.length - 1;
  if (code === 0x3c) return '&lt;'.length - 1;
  if (code === 0x3e) return '&gt;'.length - 1;
  if (code === 0xa0) return '&nbsp;'.length - 2;
  return 0;
};

const encoder = new TextEncoder();

export const byteLength = (value: string) => encoder.encode(value).length;

export const textByteLength = (doc: PMNode, from: number, to: number) => byteLength(doc.textBetween(from, to, ' '));
